import { startTransition, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { useLocalStorage } from 'react-use'
import {
  CompactSelection,
  DataEditorProps,
  DataEditorRef,
  EditableGridCell,
  GridCellKind,
  GridColumn,
  GridSelection,
  Item,
  Rectangle,
} from '@glideapps/glide-data-grid'

import { IAddColumnBody, InsertColumnPosition } from '@makyoapp/makyo-sdk/api/Tables'
import { ColumnTypes, ITableRow } from '@makyoapp/makyo-sdk/models/Table'
import { ColumnAdapter, TableAdapter } from '@makyoapp/makyo-common'

import { ICell } from '@/core/models/columns'
import constate from '@/lib/constate'
import { useDbClientContext } from '@/context/__project__/DbClientContext'
import { useGridSearchConfig } from '@/hooks/useGridSearchConfig'
import { useGridTableData } from '@/hooks/useGridTableData'
import { useParamId } from '@/hooks/useParamId'
import { useReqError } from '@/hooks/useReqError'
import { STORAGE_KEYS } from '@/shared/constants'
import { EVENT_KEYS } from '@/shared/custom-event'
import { arrayReorder, debounce } from '@/shared/utils'

import {
  getCachedColumnsData,
  parseGlideColDefList,
  removeCachedColumnData,
  setCachedColumnData,
} from '../helpers/glide-columns'

interface IGridModal {
  isOpen: boolean
  column: ColumnAdapter | null
  bounds: Rectangle | null
}

interface IGridHeaderMenuModal {
  columnId: string
  type: string
}

interface IColumnToInsert {
  positionToInsert: InsertColumnPosition
  columnID: string
}

export interface TooltipData {
  content: string | null
  bounds: Rectangle | null
}

const GLIDE_COLUMN_ROW_ID = {
  id: '_id',
  name: 'Row ID',
  type: 'row-id',
  isFrozen: true,
  width: 290,
}

/**
 * New Grid V2 Context
 */
const [GridV2Provider, useGridV2Context] = constate('GridV2Context', () => {
  const { t } = useTranslation()
  const printAPIError = useReqError()
  const tbId = useParamId('tbl') as string

  // Access DbClient
  const { dbClient, tables, tablesMap, language, setLanguage, userSpecific, setUserSpecific } = useDbClientContext()
  const tableList = tables
  const dbId = dbClient?.dbID as string
  const activeTable = tablesMap.get(tbId)
  const tableId = activeTable?.settings._id as string

  const isSnapshot = !!activeTable?.isSnapshot
  const isSnapshotOrQuery = !!activeTable?.isSnapshot || !!activeTable?.isQuery
  const isQuery = !!activeTable?.isQuery

  // Search Config
  const { searchConfig, updateSearchConfig } = useGridSearchConfig(tableId)

  // Initialize Grid Table Data
  const {
    tableAdapter,
    columns,
    setColumns,
    rows,
    filteredRowsCount,
    allRowsCount,
    isLoading,
    pagination,
    handlePaginationChange,
    totalPages,
  } = useGridTableData(tableId, searchConfig)

  // --------------------------------------------------------------------------------------//
  //                               State & Refs Handlers                                   //
  // --------------------------------------------------------------------------------------//

  const [loadingAddRow, setLoadingAddRow] = useState(false)
  const [tooltip, setTooltip] = useState<TooltipData>({ content: null, bounds: null })
  const [tableToRemove, setTableToRemove] = useState<TableAdapter | undefined>()

  const [columnClipboard, setColumnClipboard] = useState<{ tbl: string; col: string } | undefined>(undefined)
  const [columnToInsert, setColumnToInsert] = useState<IColumnToInsert | undefined>(undefined)
  const [showTableRowIDs, setShowTableRowIDs] = useLocalStorage<string[]>(STORAGE_KEYS.SHOW_TABLE_ROW_IDS, [])

  // Local Refs
  const changedRowsRef = useRef<Map<string, ITableRow>>(new Map())
  const debouncedUpdateRef = useRef<ReturnType<typeof debounce>>()

  // Initialize data editor (Grid) ref
  const dataEditorRef = useRef<(DataEditorProps & DataEditorRef) | null>(null)

  const handleGridFocus = useCallback(() => {
    dataEditorRef.current?.focus()
  }, [])

  // Initialize Context State
  const [selection, setSelection] = useState<GridSelection>({
    columns: CompactSelection.empty(),
    rows: CompactSelection.empty(),
  })

  // Grid Persisted State
  const [gridModal, setGridModal] = useLocalStorage<IGridModal>(STORAGE_KEYS.GRID_MODAL, {
    isOpen: false,
    column: null,
    bounds: null,
  } as IGridModal)

  const [isAddColumnModalOpen, setAddColumnModalOpen] = useLocalStorage<boolean>(
    STORAGE_KEYS.IS_ADD_COLUMN_MODAL_OPEN,
    false
  )

  const [modalGridHeaderMenu, setModalGridHeaderMenu] = useLocalStorage<IGridHeaderMenuModal>(
    STORAGE_KEYS.HEADER_MENU_GRID_MODAL,
    {} as IGridHeaderMenuModal
  )

  const handleCloseMenu = useCallback(() => {
    setGridModal({ isOpen: false, column: null, bounds: null })
    setModalGridHeaderMenu({ type: '', columnId: '' })
    setAddColumnModalOpen(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /**
   * Extended grid columns.
   */
  const glideColumns = useMemo(() => {
    if (!activeTable) return []

    // get cache from localStorage/cookie
    let cachedColumnsData = getCachedColumnsData()
    let cachedColumns = cachedColumnsData?.[activeTable.id] || []

    const isShowRowID = showTableRowIDs?.includes(activeTable.id)
    const columnList = [...(isShowRowID ? [GLIDE_COLUMN_ROW_ID] : []), ...columns] as ColumnAdapter[]

    let filteredColumns = columnList

    // column type filter
    if (searchConfig.columnTypes.length > 0) {
      filteredColumns = columnList.filter((c) => searchConfig.columnTypes.includes(c.type))
    }

    // column search
    if (searchConfig.type === 'column' && searchConfig.value.trim().length > 0) {
      filteredColumns = columnList.filter((c) => c.name.toLowerCase().includes(searchConfig.value.toLowerCase()))
    }

    return (
      parseGlideColDefList(filteredColumns, cachedColumns, Boolean(isSnapshotOrQuery), Boolean(isQuery))
        // filter visibility
        .filter((c) => (c.id !== '_id' ? !c.isHidden : true))
        // ensure _id is always at the first position
        .sort((a, b) => {
          if (a.id === '_id') return -1
          if (b.id === '_id') return 1

          // sort the remaining columns based on their `frozen` state
          if (!a.isFrozen && b.isFrozen) return 1
          if (a.isFrozen && !b.isFrozen) return -1
          return 0
        })
    )
    //
  }, [columns, showTableRowIDs, activeTable, isSnapshotOrQuery, isQuery, searchConfig])

  /**
   * Combined (columns and rowID) grid column
   */
  const combinedColumns = useMemo(() => {
    const rowIdColumn = glideColumns.find((col) => col.id === '_id')
    if (rowIdColumn && !columns.some((col) => col.id === '_id')) {
      return [rowIdColumn, ...columns]
    }

    return columns
  }, [glideColumns, columns])

  //--------------------------------------------------------------------------------------//
  //                                  Table Mutation                                      //
  //--------------------------------------------------------------------------------------//

  const handleRenameTable = async (tableID: string, newName: string) => {
    await activeTable?.RenameTable(tableID, newName, dbId)
  }

  //--------------------------------------------------------------------------------------//
  //                                  Rows Mutation                                       //
  //--------------------------------------------------------------------------------------//

  /**
   * Mutation: Process Bulk Updates
   *
   * this is the continuation of @see handleEditCell
   */
  const processBulkUpdates = useCallback(() => {
    if (!activeTable || changedRowsRef.current.size === 0) return

    const updatedRows = Array.from(changedRowsRef.current.values())

    try {
      activeTable.UpdateData(updatedRows, language, userSpecific)
    } catch (error) {
      console.warn('Failed to update rows, clearing cache...')
    } finally {
      changedRowsRef.current.clear()
      handleGridFocus()
    }
    //
  }, [activeTable, handleGridFocus, language, userSpecific])

  /**
   * Debounce the bulk update function from @see handleEditCell
   */
  useEffect(() => {
    debouncedUpdateRef.current = debounce(processBulkUpdates, 200)
    return () => {
      debouncedUpdateRef.current?.cancel()
    }
  }, [processBulkUpdates])

  /**
   * Mutation: Update Cell
   *
   * @note this function will handle both single and multiple rows update,
   * including `onPaste` and `onDelete` (with selection) handler by glide grid.
   * to make it optimzed, we will then throttlled this function to send the bulk update instead.
   *
   * @see processBulkUpdates fn
   */
  const handleEditCell = useCallback(
    ([col, rowIdx]: Item, gridCellData: EditableGridCell) => {
      if (!activeTable) return

      // get current column
      let currentColumn = columns.find((c) => c.id === glideColumns[col].id)

      if (!currentColumn) return

      const columnID = currentColumn.id
      const rowData = rows[rowIdx]
      const initialCell = rowData[columnID] as ICell
      const initialValueStr = typeof initialCell.value === 'string' ? initialCell.value : initialCell.toString()

      const updatedData =
        gridCellData.kind === GridCellKind.Custom ? (gridCellData.data as any).cell : gridCellData.data

      const updatedCell = (
        gridCellData.kind === GridCellKind.Custom ? updatedData : initialCell.toCell(updatedData as any)
      ) as ICell

      const updatedValueStr = typeof updatedCell.value === 'string' ? updatedCell.value : updatedCell.toString()

      const shouldUpdate = gridCellData.kind === GridCellKind.Custom || initialValueStr !== updatedValueStr

      // if value is changed, set into bulk update pool
      if (shouldUpdate) {
        const updatedRow = activeTable?.FormatToRaw([rowData])[0]
        updatedRow[columnID] = updatedCell.value as any

        // check if rowData exist in pool
        let currentPool = changedRowsRef.current.get(rowData._id)

        if (currentPool) {
          changedRowsRef.current.set(rowData._id, { ...currentPool, ...updatedRow })
        } else {
          changedRowsRef.current.set(rowData._id, updatedRow)
        }

        // Call the debounced bulk update function
        debouncedUpdateRef.current?.()
      }
    },
    [activeTable, columns, glideColumns, rows]
  )

  /**
   * Mutation: Add Row
   */
  const handleAddRow = useCallback(() => {
    if (!activeTable) return
    if (isSnapshot) return

    setLoadingAddRow(true)

    activeTable
      .AddRow({} as ITableRow)
      .then(() => {
        if (filteredRowsCount >= pagination.perPage) {
          const lastPage = Math.ceil(filteredRowsCount / pagination.perPage) - 1
          handlePaginationChange(lastPage, pagination.perPage)
        }
      })
      .catch(printAPIError)
      .finally(() => {
        setLoadingAddRow(false)
      })

    //
  }, [activeTable, pagination.perPage, filteredRowsCount, handlePaginationChange, printAPIError, isSnapshot])

  /**
   * Mutation Delete Row Selection
   */
  const handleDeleteRowsSelection = useCallback(async () => {
    if (!activeTable) return 0

    const targetRows = selection?.rows.toArray() ?? []

    if (!targetRows.length) return 0

    const rowIds = targetRows.map((index) => {
      let row = rows[index]
      return row._id
    })

    await activeTable?.DeleteRows(rowIds, language)

    // reset selection
    setSelection({
      columns: CompactSelection.empty(),
      rows: CompactSelection.empty(),
    })

    return rowIds.length
    //
  }, [activeTable, language, rows, selection?.rows])

  //--------------------------------------------------------------------------------------//
  //                                  Columns Mutation                                    //
  //--------------------------------------------------------------------------------------//

  /**
   * [Columns Mutation]: Add Column
   */
  const handleAddColumn = async (tableId: string, newColumn: IAddColumnBody) => {
    if (!newColumn || !activeTable) return

    const column = await activeTable.AddColumn(newColumn).catch(printAPIError)

    // prevent the custom cell become blank after re-render
    handleGridFocus()

    return column
  }

  /**
   * [Columns Mutation]: Update Column Settings
   */
  const handleUpdateColumnSettings = async (tableId: string, columnId: string, body: IAddColumnBody) => {
    if (!tableId || !activeTable) return

    const updatedColumn = await activeTable?.UpdateColumnSettings(tableId, columnId, body, dbId)

    // remove the column if the type is changing
    if (updatedColumn && updatedColumn._Type !== body._Type) {
      setColumns((prev) => prev.filter((c) => c.id !== columnId))
    }

    // prevent the custom cell become blank after re-render
    handleGridFocus()
  }

  /**
   * [Columns Mutation]: Delete Column
   */
  const handleDeleteColumn = async (tableId: string, columnId: string) => {
    if (!tableId || !columnId || !activeTable) return

    await activeTable.DeleteColumn(columnId)
    handleGridFocus()
  }

  /**
   * [Columns Mutation]: Delete Multiple Columns (including dependent columns)
   */
  const handleDeleteMultipleColumns = async (
    dependents: Map<
      string,
      {
        connections: Set<string>
        lookups: Set<string>
      }
    >
  ) => {
    if (!dbClient) return

    const columnsDeletePromises = Array.from(dependents.entries()).flatMap(([tableId, { connections, lookups }]) => [
      ...Array.from(connections).map((connectionId) => dbClient.provider.deleteColumn(tableId, connectionId, dbId)),
      ...Array.from(lookups).map((lookupId) => dbClient.provider.deleteColumn(tableId, lookupId, dbId)),
    ])

    await Promise.all(columnsDeletePromises)
  }

  /**
   * [Columns Mutation]: Duplicate Column
   */
  const handleDuplicateColumn = async (columnID: string, newColumnName: string) => {
    if (!columnID || !activeTable) return

    await activeTable?.DuplicateColumn(columnID, newColumnName)

    // prevent the custom cell become blank after re-render
    handleGridFocus()
  }

  /**
   * [Columns Mutation]: Hide Column
   */
  const handleHideColumn = (columnId: string, isHidden: boolean) => {
    if (!columnId || !activeTable) return

    activeTable.HideColumn(columnId, isHidden)

    // prevent the custom cell become blank after re-render
    handleGridFocus()
  }

  const handleHideAllColumns = (isHidden: boolean) => {
    if (!activeTable) return

    activeTable.HideAllColumns(isHidden)

    // prevent the custom cell become blank after re-render
    handleGridFocus()
  }

  /**
   * [Columns Mutation]: Insert Column
   */
  const handleInsertColumn = async (
    tableID: string,
    targetColumnID: string,
    position: InsertColumnPosition,
    newColumnBody: IAddColumnBody
  ) => {
    if (!tableID || !targetColumnID || !activeTable || !position) return

    // find current column index
    const currentColumnIndex = activeTable.GetColumns().findIndex((c) => c.id === targetColumnID)

    if (currentColumnIndex === -1) return

    await activeTable?.InsertColumn(tableID, targetColumnID, position, newColumnBody, dbId)

    // prevent the custom cell become blank after re-render
    dataEditorRef.current?.focus()
  }

  /**
   * [Columns Mutation]: Paste Column
   *
   * @info WIP
   *
   * ! issues:
   * - problem with copying connection/lookup/function (the relative will always be different)
   */
  const handlePasteColumn = async (targetColumnID?: string, position?: InsertColumnPosition) => {
    if (!columnClipboard || !activeTable || !dbClient) return

    if (activeTable.isSnapshot) return

    // sanity check
    const fromTable = dbClient.tablesMap.get(columnClipboard.tbl)
    if (!fromTable) return

    const fromColumn = fromTable.columnsMap.get(columnClipboard.col)
    if (!fromColumn) return

    // #1 - if users try to copy into query, needs to be advanced columns
    if (activeTable.isQuery) {
      if (
        ![
          ColumnTypes.Connection,
          ColumnTypes.Lookup,
          ColumnTypes.Function,
          ColumnTypes.Math,
          ColumnTypes.Template,
          ColumnTypes.SingleCell,
        ].includes(fromColumn._Type)
      ) {
        toast.error(t('You can only copy advanced columns into query!'))
        return
      }
    }

    await activeTable.PasteColumn(columnClipboard.tbl, columnClipboard.col, targetColumnID, position)

    window.dispatchEvent(new CustomEvent(EVENT_KEYS.DB_CLIENT_RELOAD))

    setColumnClipboard(undefined)
    toast.success(t('Column pasted successfully'))
  }

  //--------------------------------------------------------------------------------------//
  //                                  GridColumns Mutation                                //
  //--------------------------------------------------------------------------------------//

  /**
   * [GridColumns Mutation]: Handles resizing of a column
   *
   * @todo: prevent re-render when resizing
   */
  const handleResizeColumn = useCallback(
    (column: GridColumn, newSize: number, grow: number) => {
      if (!tbId) return

      setColumns((prev) => {
        const index = prev.findIndex((ci) => ci.id === column.id)
        const newArray = [...prev]
        newArray.splice(index, 1, {
          ...prev[index],
          // @ts-ignore
          width: newSize + grow,
        })

        startTransition(() => {
          // Update the cache with the new column sizes
          let cachedColumnsData = getCachedColumnsData()
          let cachedColumns = cachedColumnsData?.[tbId] || []
          let updatedCache = {
            ...cachedColumnsData,
            [tbId]: parseGlideColDefList(newArray, cachedColumns, Boolean(isSnapshotOrQuery), Boolean(isQuery)),
          }
          setCachedColumnData(updatedCache)
        })

        return newArray
      })
    },
    [tbId, setColumns, isSnapshotOrQuery, isQuery]
  )

  /**
   * [GridColumns Mutation]: Handle reset column width
   */
  const handleResetColumnsWidth = () => {
    removeCachedColumnData()

    setColumns((prev) => {
      return prev.map((p) => {
        const copy = { ...p }
        // @ts-ignore
        delete copy['width']
        return copy
      })
    })
  }

  /**
   * [GridColumns Mutation]: Reorder Column Method
   */
  const handleReorderColumn = useCallback(
    (startIndex: number, endIndex: number) => {
      if (!dbId || !dbClient) return

      const originalColumnId = glideColumns[startIndex].id
      const targetColumnId = glideColumns[endIndex].id

      if (!originalColumnId || !targetColumnId) return

      const table = dbClient?.tablesMap.get(tbId)
      if (!table) return

      const sourceColumn = table?.columnsMap.get(originalColumnId)
      const targetColumn = table?.columnsMap.get(targetColumnId)
      const isSourceOriginal = Boolean(sourceColumn?._IsSourceColumn)
      const isTargeOriginal = Boolean(targetColumn?._IsSourceColumn)

      // for query table, prevent reordering if original or target column is original
      if (isSourceOriginal || isTargeOriginal) {
        return
      }

      const originalPositionIndex = columns.findIndex((c) => c.id === originalColumnId)
      const targetPositionIndex = columns.findIndex((c) => c.id === targetColumnId)

      const newColumnsOrder = arrayReorder(columns, originalPositionIndex, targetPositionIndex).map((c) => c.id)

      table.ColumnReorder(newColumnsOrder, dbId)

      // prevent the custom cell become blank after re-render
      handleGridFocus()
    },
    [columns, dbClient, dbId, glideColumns, handleGridFocus, tbId]
  )

  /**
   * [GridColumns Mutation]: Freeze column
   */
  const handleFreezeColumn = (colId: string) => {
    if (!colId || !activeTable) return

    activeTable.FreezeColumn(colId, true)

    setGridModal({ isOpen: false, column: null, bounds: null })

    // prevent the custom cell become blank after re-render
    handleGridFocus()
  }

  return {
    // States & Updater
    isLoading,
    activeTable,
    columns,
    setColumns,
    rows,
    filteredRowsCount,
    allRowsCount,
    isAddColumnModalOpen,
    setAddColumnModalOpen,
    gridModal,
    setGridModal,
    userLanguage: language,
    setUserLanguage: setLanguage,
    userSpecific,
    setUserSpecific,
    modalGridHeaderMenu,
    setModalGridHeaderMenu,
    glideColumns,
    combinedColumns,
    columnToInsert,
    setColumnToInsert,
    setShowTableRowIDs,
    showTableRowIDs,
    handleCloseMenu,
    selection,
    setSelection,
    loadingAddRow,
    handleGridFocus,
    tooltip,
    setTooltip,
    columnClipboard,
    setColumnClipboard,
    handlePasteColumn,

    // Global Method
    tableToRemove,
    setTableToRemove,

    // Advanced Handlers
    handleEditCell,
    handleAddRow,
    handleAddColumn,
    handleUpdateColumnSettings,
    handleResizeColumn,
    handleResetColumnsWidth,
    handleReorderColumn,
    handleFreezeColumn,
    handleDeleteColumn,
    handleDeleteMultipleColumns,
    handleHideColumn,
    handleHideAllColumns,
    handleDuplicateColumn,
    handleInsertColumn,
    handleDeleteRowsSelection,
    handleRenameTable,

    // pagination
    pagination,
    handlePaginationChange,
    totalPages,

    // Table
    dataEditorRef,
    table: tableAdapter,
    tableList,
    tableId,

    // DB
    dbId,

    // Search Config
    searchConfig,
    updateSearchConfig,
  }
})

export { GridV2Provider, useGridV2Context }
